import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

import { projectAuth } from "@/firebase/firebase";

const heartbeatInterval = 30 * 1000;

const initialsForName = name => {
  const words = String(name ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) { return "A"; }

  return [words[0], words.at(-1)]
    .slice(0, words.length === 1 ? 1 : 2)
    .map(word => word.charAt(0).toUpperCase())
    .join("");
};

export const useContentEditLease = ({
  contentId,
  adminId,
  adminName,
  contentLabel,
  claimEditLease,
  listenToEditLease,
  releaseEditLease
}) => {
  const lease = ref(null);
  const leaseReady = ref(false);
  const leaseError = ref("");
  const clock = ref(Date.now());

  let unsubscribe = null;
  let heartbeat = null;
  let activeContentId = "";
  let leaseGeneration = 0;

  const normalizedContentId = computed(() => String(contentId.value ?? "").trim());
  const currentAdminId = computed(() => projectAuth.currentUser?.uid ?? adminId.value);

  const activeLease = computed(() => {
    clock.value;
    if (!lease.value) { return null; }

    const expiresAt = lease.value.expiresAt?.toMillis?.() ?? 0;
    return expiresAt > Date.now() ? lease.value : null;
  });

  const editingBlocked = computed(() => {
    if (!normalizedContentId.value) { return false; }
    if (!leaseReady.value || leaseError.value) { return true; }
    return activeLease.value?.adminId !== currentAdminId.value;
  });

  const displayedEditor = computed(() => activeLease.value ?? {
    adminId: currentAdminId.value,
    adminName: adminName.value || "Admin"
  });
  const editorInitials = computed(() => initialsForName(displayedEditor.value.adminName));
  const editorName = computed(() => displayedEditor.value.adminName || "Admin");
  const isCurrentEditor = computed(() => !editingBlocked.value);

  const editorStatus = computed(() => {
    if (leaseError.value) { return "Editor unavailable"; }
    if (normalizedContentId.value && !leaseReady.value) { return "Checking editor..."; }
    return isCurrentEditor.value ? "You are editing" : `${editorName.value} is editing`;
  });

  const stopLease = async release => {
    const contentToRelease = activeContentId;
    activeContentId = "";
    leaseGeneration += 1;

    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }

    if (heartbeat) {
      clearInterval(heartbeat);
      heartbeat = null;
    }

    if (release && contentToRelease) {
      try {
        await releaseEditLease(contentToRelease);
      } catch (error) {
        console.warn(`Unable to release the ${contentLabel} editor lease:`, error);
      }
    }
  };

  const claimLease = async (requestedContentId, generation) => {
    try {
      const result = await claimEditLease(requestedContentId, adminName.value);
      if (generation !== leaseGeneration) { return; }

      lease.value = result.lease;
      leaseReady.value = true;
      leaseError.value = "";
    } catch (error) {
      if (generation !== leaseGeneration) { return; }

      console.error(`Unable to claim the ${contentLabel} editor lease:`, error);
      leaseReady.value = true;
      leaseError.value =
        "Editing is unavailable until the editor session can be verified.";
    }
  };

  const startLease = async requestedContentId => {
    await stopLease(true);
    lease.value = null;
    leaseError.value = "";

    if (!requestedContentId) {
      leaseReady.value = true;
      return;
    }

    leaseReady.value = false;
    activeContentId = requestedContentId;
    const generation = leaseGeneration;

    unsubscribe = listenToEditLease(requestedContentId, (nextLease, error) => {
      if (generation !== leaseGeneration) { return; }

      if (error) {
        leaseError.value =
          "Editing is unavailable until the editor session can be verified.";
        leaseReady.value = true;
        return;
      }

      lease.value = nextLease;
      if (!nextLease) { claimLease(requestedContentId, generation); }
    });

    await claimLease(requestedContentId, generation);

    heartbeat = setInterval(() => {
      clock.value = Date.now();
      claimLease(requestedContentId, generation);
    }, heartbeatInterval);
  };

  watch(
    normalizedContentId,
    requestedContentId => startLease(requestedContentId),
    { immediate: true }
  );

  const handlePageHide = () => stopLease(true);
  onMounted(() => window.addEventListener("pagehide", handlePageHide));
  onBeforeUnmount(() => {
    window.removeEventListener("pagehide", handlePageHide);
    stopLease(true);
  });

  return {
    editingBlocked,
    editorInitials,
    editorName,
    editorStatus,
    isCurrentEditor,
    leaseError
  };
};
